import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Form, Tabs, Tab, Dropdown } from 'react-bootstrap';
import { FaPlay, FaPause, FaStepBackward, FaStepForward, FaList, FaMusic, FaRandom } from 'react-icons/fa';
import { usePlayer } from '../context/PlayerContext';
import { useLibrary } from '../context/LibraryContext';
import api from '../utils/api';
import '../styles/DjMixer.css';

const DjMixer = () => {
  // État des platines
  const [deck1, setDeck1] = useState({
    song: null,
    isPlaying: false,
    volume: 80,
    progress: 0,
    currentPlaylist: null,
    playlistIndex: -1
  });
  
  const [deck2, setDeck2] = useState({
    song: null,
    isPlaying: false,
    volume: 80,
    progress: 0,
    currentPlaylist: null,
    playlistIndex: -1
  });
  
  // Crossfader (0 = platine 1 uniquement, 100 = platine 2 uniquement)
  const [crossfader, setCrossfader] = useState(50);
  
  // État pour le mix automatique
  const [autoMixEnabled, setAutoMixEnabled] = useState(false);
  const [autoMixTimeBeforeEnd, setAutoMixTimeBeforeEnd] = useState(10); // Secondes avant la fin pour lancer le mix
  
  // Interface active (songs ou playlists)
  const [activeTab, setActiveTab] = useState('songs');
  
  // État pour suivre quelles playlists ont leurs chansons affichées
  const [expandedPlaylists, setExpandedPlaylists] = useState({});
  
  // Références aux éléments audio
  const audioRef1 = useRef(null);
  const audioRef2 = useRef(null);
  
  // Références aux disques
  const disc1Ref = useRef(null);
  const disc2Ref = useRef(null);
  
  // Contexte du lecteur global et bibliothèque
  const { currentSong } = usePlayer();
  const { songs, playlists, loading, fetchSongs, fetchPlaylists } = useLibrary();
  
  // Chargement des données si nécessaire
  useEffect(() => {
    if (songs.length === 0) {
      fetchSongs();
    }
    if (playlists.length === 0) {
      fetchPlaylists();
    }
  }, [songs.length, playlists.length, fetchSongs, fetchPlaylists]);
  
  // Vérifier si une playlist a été sélectionnée depuis la page des playlists
  useEffect(() => {
    const selectedPlaylist = localStorage.getItem('selectedPlaylistForDjMixer');
    if (selectedPlaylist && playlists.length > 0) {
      try {
        const playlist = JSON.parse(selectedPlaylist);
        // Trouver la playlist correspondante dans la liste des playlists chargées
        const matchedPlaylist = playlists.find(p => p._id === playlist._id);
        if (matchedPlaylist) {
          // Charger la playlist dans la platine A par défaut
          loadPlaylist(matchedPlaylist, 1, 0);
          // Définir l'onglet actif sur "playlists"
          setActiveTab('playlists');
        }
        // Nettoyer le localStorage
        localStorage.removeItem('selectedPlaylistForDjMixer');
      } catch (error) {
        console.error("Erreur lors du chargement de la playlist sélectionnée:", error);
      }
    }
  }, [playlists]);
  
  // Gestion de la rotation des disques
  useEffect(() => {
    let animationFrame1;
    let animationFrame2;
    let rotation1 = 0;
    let rotation2 = 0;
    
    const animateDisc1 = () => {
      if (deck1.isPlaying && disc1Ref.current) {
        rotation1 += 0.2;
        disc1Ref.current.style.transform = `rotate(${rotation1}deg)`;
        animationFrame1 = requestAnimationFrame(animateDisc1);
      }
    };
    
    const animateDisc2 = () => {
      if (deck2.isPlaying && disc2Ref.current) {
        rotation2 += 0.2;
        disc2Ref.current.style.transform = `rotate(${rotation2}deg)`;
        animationFrame2 = requestAnimationFrame(animateDisc2);
      }
    };
    
    if (deck1.isPlaying) {
      animationFrame1 = requestAnimationFrame(animateDisc1);
    }
    
    if (deck2.isPlaying) {
      animationFrame2 = requestAnimationFrame(animateDisc2);
    }
    
    return () => {
      cancelAnimationFrame(animationFrame1);
      cancelAnimationFrame(animationFrame2);
    };
  }, [deck1.isPlaying, deck2.isPlaying]);
  
  // Mise à jour du volume en fonction du crossfader
  useEffect(() => {
    if (audioRef1.current) {
      audioRef1.current.volume = (deck1.volume / 100) * ((100 - crossfader) / 100);
    }
    
    if (audioRef2.current) {
      audioRef2.current.volume = (deck2.volume / 100) * (crossfader / 100);
    }
  }, [crossfader, deck1.volume, deck2.volume]);
  
  // Mise à jour de la progression de lecture et gestion du mix automatique
  useEffect(() => {
    const updateProgress = () => {
      if (audioRef1.current && deck1.isPlaying) {
        const currentTime = audioRef1.current.currentTime;
        const duration = audioRef1.current.duration;
        const progress = (currentTime / duration) * 100;
        
        setDeck1(prev => ({
          ...prev,
          progress: progress
        }));
        
        // Gestion du mix automatique pour la platine 1
        if (autoMixEnabled && deck1.currentPlaylist && deck1.currentPlaylist.songs && 
            deck1.playlistIndex >= 0 && 
            !isNaN(duration) && duration > 0 && 
            duration - currentTime <= autoMixTimeBeforeEnd) {
          
          // Vérifier si nous n'avons pas déjà déclenché le mix pour cette fin de chanson
          const timeRemaining = duration - currentTime;
          console.log(`Auto-mix check: Platine A - Temps restant: ${timeRemaining.toFixed(2)}s, Seuil: ${autoMixTimeBeforeEnd}s`);
          
          // Utiliser un ID unique pour cette fin de chanson pour éviter les déclenchements multiples
          const mixId = `deck1-${deck1.song?._id}-${Math.floor(duration)}`;
          if (timeRemaining <= autoMixTimeBeforeEnd && !window[mixId]) {
            // Marquer cette fin de chanson comme déjà traitée
            window[mixId] = true;
            
            console.log('Auto-mix: Préparation du mix depuis platine A vers platine B');
            // Charger la chanson suivante dans la platine 2
            const nextIndex = (deck1.playlistIndex + 1) % deck1.currentPlaylist.songs.length;
            console.log(`Auto-mix: Chargement de l'index ${nextIndex} de la playlist dans la platine B`);
            
            loadPlaylist(deck1.currentPlaylist, 2, nextIndex);
            
            // Démarrer la lecture après un court délai
            setTimeout(() => {
              if (audioRef2.current) {
                console.log('Auto-mix: Démarrage de la lecture sur platine B');
                togglePlay(2);
                // Commencer à ajuster progressivement le crossfader
                startCrossfade(1, 2);
              }
            }, 1000);
          }
        }
      }
      
      if (audioRef2.current && deck2.isPlaying) {
        const currentTime = audioRef2.current.currentTime;
        const duration = audioRef2.current.duration;
        const progress = (currentTime / duration) * 100;
        
        setDeck2(prev => ({
          ...prev,
          progress: progress
        }));
        
        // Gestion du mix automatique pour la platine 2
        if (autoMixEnabled && deck2.currentPlaylist && deck2.currentPlaylist.songs && 
            deck2.playlistIndex >= 0 && 
            !isNaN(duration) && duration > 0 && 
            duration - currentTime <= autoMixTimeBeforeEnd) {
          
          // Vérifier si nous n'avons pas déjà déclenché le mix pour cette fin de chanson
          const timeRemaining = duration - currentTime;
          console.log(`Auto-mix check: Platine B - Temps restant: ${timeRemaining.toFixed(2)}s, Seuil: ${autoMixTimeBeforeEnd}s`);
          
          // Utiliser un ID unique pour cette fin de chanson pour éviter les déclenchements multiples
          const mixId = `deck2-${deck2.song?._id}-${Math.floor(duration)}`;
          if (timeRemaining <= autoMixTimeBeforeEnd && !window[mixId]) {
            // Marquer cette fin de chanson comme déjà traitée
            window[mixId] = true;
            
            console.log('Auto-mix: Préparation du mix depuis platine B vers platine A');
            // Charger la chanson suivante dans la platine 1
            const nextIndex = (deck2.playlistIndex + 1) % deck2.currentPlaylist.songs.length;
            console.log(`Auto-mix: Chargement de l'index ${nextIndex} de la playlist dans la platine A`);
            
            loadPlaylist(deck2.currentPlaylist, 1, nextIndex);
            
            // Démarrer la lecture après un court délai
            setTimeout(() => {
              if (audioRef1.current) {
                console.log('Auto-mix: Démarrage de la lecture sur platine A');
                togglePlay(1);
                // Commencer à ajuster progressivement le crossfader
                startCrossfade(2, 1);
              }
            }, 1000);
          }
        }
      }
    };
    
    const interval = setInterval(updateProgress, 500); // Mise à jour plus fréquente
    return () => clearInterval(interval);
  }, [
    deck1.isPlaying, deck2.isPlaying, autoMixEnabled, autoMixTimeBeforeEnd,
    deck1.currentPlaylist, deck2.currentPlaylist, deck1.playlistIndex, deck2.playlistIndex,
    deck1.song, deck2.song
  ]);
  
  // Fonction pour effectuer un crossfade progressif entre deux platines
  const startCrossfade = (fromDeck, toDeck) => {
    console.log(`Début du crossfade de la platine ${fromDeck} vers la platine ${toDeck}`);
    
    // Valeur initiale du crossfader
    const startValue = fromDeck === 1 ? 0 : 100;
    // Valeur finale du crossfader
    const endValue = fromDeck === 1 ? 100 : 0;
    // Durée du crossfade en millisecondes
    const duration = 8000; // Durée plus longue pour un mix plus doux
    // Nombre d'étapes pour le crossfade
    const steps = 80;
    // Intervalle entre chaque étape
    const interval = duration / steps;
    // Valeur à ajouter à chaque étape
    const step = (endValue - startValue) / steps;
    
    let currentStep = 0;
    let currentValue = startValue;
    
    // Assurons-nous que le crossfader est bien positionné au début
    setCrossfader(startValue);
    
    const crossfadeInterval = setInterval(() => {
      currentStep++;
      currentValue += step;
      const newValue = Math.round(currentValue);
      
      console.log(`Crossfade: Étape ${currentStep}/${steps}, Valeur: ${newValue}`);
      setCrossfader(newValue);
      
      // Arrêter le crossfade après avoir atteint le nombre d'étapes
      if (currentStep >= steps) {
        clearInterval(crossfadeInterval);
        console.log('Crossfade terminé');
        
        // Arrêter la platine source après le crossfade
        if (fromDeck === 1 && audioRef1.current) {
          console.log('Arrêt de la platine A après crossfade');
          audioRef1.current.pause();
          setDeck1(prev => ({ ...prev, isPlaying: false }));
        } else if (fromDeck === 2 && audioRef2.current) {
          console.log('Arrêt de la platine B après crossfade');
          audioRef2.current.pause();
          setDeck2(prev => ({ ...prev, isPlaying: false }));
        }
      }
    }, interval);
  };
  
  // Fonction pour activer/désactiver le mix automatique
  const toggleAutoMix = () => {
    const newState = !autoMixEnabled;
    console.log(`Mix automatique ${newState ? 'activé' : 'désactivé'} avec un délai de ${autoMixTimeBeforeEnd} secondes`);
    setAutoMixEnabled(newState);
    
    // Réinitialiser les marqueurs de mix
    if (!newState) {
      Object.keys(window).forEach(key => {
        if (key.startsWith('deck1-') || key.startsWith('deck2-')) {
          delete window[key];
        }
      });
    }
  };
  
  // Fonction pour charger une chanson dans une platine
  const loadSong = (song, deckNumber) => {
    const deck = deckNumber === 1 ? deck1 : deck2;
    const setDeck = deckNumber === 1 ? setDeck1 : setDeck2;
    const audioRef = deckNumber === 1 ? audioRef1 : audioRef2;
    
    setDeck({
      ...deck,
      song: song,
      isPlaying: false,
      progress: 0,
      // Réinitialiser la playlist active si on charge une chanson individuelle
      currentPlaylist: null,
      playlistIndex: -1
    });
    
    if (audioRef.current) {
      audioRef.current.src = song.filePath ? 
        `/api/songs/stream/${song._id}` : 
        song.youtubeUrl;
      audioRef.current.load();
    }
  };
  
  // Afficher/masquer les chansons d'une playlist
  const togglePlaylistSongs = (playlistId) => {
    // Trouver la playlist pour le débogage
    const playlist = playlists.find(p => p._id === playlistId);
    if (playlist) {
      console.log('Playlist sélectionnée:', playlist.name);
      console.log('Chansons dans la playlist:', playlist.songs);
    }
    
    setExpandedPlaylists(prev => ({
      ...prev,
      [playlistId]: !prev[playlistId]
    }));
  };

  // Chargement d'une playlist dans une platine
  const loadPlaylist = (playlist, deckNumber, startIndex = 0) => {
    if (!playlist || !playlist.songs || playlist.songs.length === 0) {
      console.error('Playlist vide ou invalide:', playlist);
      return;
    }
    
    // Récupérer l'ID de la chanson ou l'objet chanson
    const songItem = playlist.songs[startIndex];
    console.log('Élément de chanson dans la playlist:', songItem);
    
    if (!songItem) {
      console.error('Chanson invalide à l\'index', startIndex);
      return;
    }
    
    // Si songItem est juste un ID, chercher la chanson complète dans la liste des chansons
    let fullSong;
    
    if (typeof songItem === 'string' || !songItem.filePath) {
      // C'est probablement juste un ID, cherchons la chanson complète
      const songId = typeof songItem === 'string' ? songItem : songItem._id;
      fullSong = songs.find(s => s._id === songId);
      console.log('Chanson complète trouvée:', fullSong);
    } else {
      // C'est déjà un objet chanson complet
      fullSong = songItem;
    }
    
    if (!fullSong) {
      console.error('Impossible de trouver les données complètes de la chanson:', songItem);
      return;
    }
    
    const deck = deckNumber === 1 ? deck1 : deck2;
    const setDeck = deckNumber === 1 ? setDeck1 : setDeck2;
    const audioRef = deckNumber === 1 ? audioRef1 : audioRef2;
    
    setDeck({
      ...deck,
      song: fullSong,
      isPlaying: false,
      progress: 0,
      currentPlaylist: playlist,
      playlistIndex: startIndex
    });
    
    if (audioRef.current) {
      try {
        // Vérifier si la chanson a un chemin de fichier ou une URL YouTube
        if (fullSong.filePath) {
          audioRef.current.src = `/api/songs/stream/${fullSong._id}`;
          console.log('URL de streaming:', `/api/songs/stream/${fullSong._id}`);
        } else if (fullSong.youtubeUrl) {
          audioRef.current.src = fullSong.youtubeUrl;
          console.log('URL YouTube:', fullSong.youtubeUrl);
        } else {
          console.error('La chanson n\'a ni filePath ni youtubeUrl:', fullSong);
          return;
        }
        audioRef.current.load();
      } catch (error) {
        console.error('Erreur lors du chargement de l\'audio:', error);
      }
    } else {
      console.error('Référence audio non disponible');
    }
  };
  
  // Passer à la chanson suivante dans la playlist
  const nextSongInPlaylist = (deckNumber) => {
    const deck = deckNumber === 1 ? deck1 : deck2;
    const setDeck = deckNumber === 1 ? setDeck1 : setDeck2;
    
    if (!deck.currentPlaylist || !deck.currentPlaylist.songs || deck.playlistIndex < 0) return;
    
    const nextIndex = (deck.playlistIndex + 1) % deck.currentPlaylist.songs.length;
    loadPlaylist(deck.currentPlaylist, deckNumber, nextIndex);
  };
  
  // Passer à la chanson précédente dans la playlist
  const prevSongInPlaylist = (deckNumber) => {
    const deck = deckNumber === 1 ? deck1 : deck2;
    const setDeck = deckNumber === 1 ? setDeck1 : setDeck2;
    
    if (!deck.currentPlaylist || !deck.currentPlaylist.songs || deck.playlistIndex < 0) return;
    
    const prevIndex = (deck.playlistIndex - 1 + deck.currentPlaylist.songs.length) % deck.currentPlaylist.songs.length;
    loadPlaylist(deck.currentPlaylist, deckNumber, prevIndex);
  };
  
  // Fonction pour jouer/pauser une platine
  const togglePlay = (deckNumber) => {
    if (deckNumber === 1) {
      if (deck1.song) {
        if (deck1.isPlaying) {
          audioRef1.current.pause();
          setDeck1(prev => ({
            ...prev,
            isPlaying: false
          }));
        } else {
          // Utilisation de try/catch pour gérer l'erreur de lecture
          try {
            const playPromise = audioRef1.current.play();
            
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  setDeck1(prev => ({
                    ...prev,
                    isPlaying: true
                  }));
                })
                .catch(error => {
                  console.error('Erreur de lecture audio:', error);
                  setDeck1(prev => ({
                    ...prev,
                    isPlaying: false
                  }));
                });
            }
          } catch (error) {
            console.error('Erreur lors de la lecture:', error);
          }
        }
      }
    } else {
      if (deck2.song) {
        if (deck2.isPlaying) {
          audioRef2.current.pause();
          setDeck2(prev => ({
            ...prev,
            isPlaying: false
          }));
        } else {
          // Utilisation de try/catch pour gérer l'erreur de lecture
          try {
            const playPromise = audioRef2.current.play();
            
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  setDeck2(prev => ({
                    ...prev,
                    isPlaying: true
                  }));
                })
                .catch(error => {
                  console.error('Erreur de lecture audio:', error);
                  setDeck2(prev => ({
                    ...prev,
                    isPlaying: false
                  }));
                });
            }
          } catch (error) {
            console.error('Erreur lors de la lecture:', error);
          }
        }
      }
    }
  };
  
  // Fonction pour changer le volume d'une platine
  const changeVolume = (deckNumber, value) => {
    if (deckNumber === 1) {
      setDeck1(prev => ({
        ...prev,
        volume: value
      }));
      
      if (audioRef1.current) {
        audioRef1.current.volume = (value / 100) * ((100 - crossfader) / 100);
      }
    } else {
      setDeck2(prev => ({
        ...prev,
        volume: value
      }));
      
      if (audioRef2.current) {
        audioRef2.current.volume = (value / 100) * (crossfader / 100);
      }
    }
  };
  
  // Fonction pour changer la position de lecture
  const changeProgress = (deckNumber, value) => {
    if (deckNumber === 1 && audioRef1.current && deck1.song) {
      const newTime = (value / 100) * audioRef1.current.duration;
      audioRef1.current.currentTime = newTime;
      setDeck1(prev => ({
        ...prev,
        progress: value
      }));
    } else if (deckNumber === 2 && audioRef2.current && deck2.song) {
      const newTime = (value / 100) * audioRef2.current.duration;
      audioRef2.current.currentTime = newTime;
      setDeck2(prev => ({
        ...prev,
        progress: value
      }));
    }
  };
  
  // Formatage du temps de lecture
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };
  
  return (
    <Container fluid className="dj-mixer-container">
      <Row className="mixer-header">
        <Col>
          <h1>DJ Mixer</h1>
          <p>Sélectionnez deux chansons et mixez-les</p>
        </Col>
      </Row>
      
      <div className="dj-deck-container">
        <div className="turntables-row">
          {/* Platine 1 */}
          <div className="turntable-container deck-left">
            <div className="disc-container">
              <div 
                ref={disc1Ref} 
                className="disc" 
                style={{ 
                  backgroundImage: deck1.song ? 
                    `url(${deck1.song.coverArt || '/images/vinyl-red.svg'})` : 
                    'url(/images/vinyl-red.svg)'
                }}
              >
                <div className="disc-center"></div>
              </div>
            </div>
            
            <div className="song-info">
              <h3>{deck1.song ? deck1.song.title : 'Aucune chanson'}</h3>
              <p>{deck1.song ? deck1.song.artist : 'Sélectionnez une chanson'}</p>
              {deck1.currentPlaylist && (
                <div className="playlist-info">
                  <span className="playlist-badge">
                    {deck1.currentPlaylist.name} ({deck1.playlistIndex + 1}/{deck1.currentPlaylist.songs.length})
                  </span>
                </div>
              )}
            </div>
            
            <div className="deck-controls">
              <div className="deck-controls-buttons">
                {deck1.currentPlaylist && (
                  <Button 
                    variant="outline-light" 
                    className="control-btn-small"
                    onClick={() => prevSongInPlaylist(1)}
                  >
                    <FaStepBackward />
                  </Button>
                )}
                
                <Button 
                  variant="outline-light" 
                  className="control-btn"
                  onClick={() => togglePlay(1)}
                >
                  {deck1.isPlaying ? <FaPause /> : <FaPlay />}
                </Button>
                
                {deck1.currentPlaylist && (
                  <Button 
                    variant="outline-light" 
                    className="control-btn-small"
                    onClick={() => nextSongInPlaylist(1)}
                  >
                    <FaStepForward />
                  </Button>
                )}
              </div>
              
              <div className="progress-container">
                <span className="time-display">{audioRef1.current ? formatTime(audioRef1.current.currentTime) : '0:00'}</span>
                <Form.Range 
                  value={deck1.progress}
                  onChange={(e) => changeProgress(1, parseInt(e.target.value))}
                  className="progress-bar"
                />
                <span className="time-display">
                  {audioRef1.current && !isNaN(audioRef1.current.duration) 
                    ? formatTime(audioRef1.current.duration) 
                    : '0:00'}
                </span>
              </div>
              
              <div className="volume-container">
                <Form.Range 
                  value={deck1.volume}
                  onChange={(e) => changeVolume(1, parseInt(e.target.value))}
                  className="volume-slider"
                />
              </div>
            </div>
            
            <audio ref={audioRef1} />
          </div>
          
          {/* Platine 2 */}
          <div className="turntable-container deck-right">
            <div className="disc-container">
              <div 
                ref={disc2Ref} 
                className="disc" 
                style={{ 
                  backgroundImage: deck2.song ? 
                    `url(${deck2.song.coverArt || '/images/vinyl-blue.svg'})` : 
                    'url(/images/vinyl-blue.svg)'
                }}
              >
                <div className="disc-center"></div>
              </div>
            </div>
            
            <div className="song-info">
              <h3>{deck2.song ? deck2.song.title : 'Aucune chanson'}</h3>
              <p>{deck2.song ? deck2.song.artist : 'Sélectionnez une chanson'}</p>
              {deck2.currentPlaylist && (
                <div className="playlist-info">
                  <span className="playlist-badge">
                    {deck2.currentPlaylist.name} ({deck2.playlistIndex + 1}/{deck2.currentPlaylist.songs.length})
                  </span>
                </div>
              )}
            </div>
            
            <div className="deck-controls">
              <div className="deck-controls-buttons">
                {deck2.currentPlaylist && (
                  <Button 
                    variant="outline-light" 
                    className="control-btn-small"
                    onClick={() => prevSongInPlaylist(2)}
                  >
                    <FaStepBackward />
                  </Button>
                )}
                
                <Button 
                  variant="outline-light" 
                  className="control-btn"
                  onClick={() => togglePlay(2)}
                >
                  {deck2.isPlaying ? <FaPause /> : <FaPlay />}
                </Button>
                
                {deck2.currentPlaylist && (
                  <Button 
                    variant="outline-light" 
                    className="control-btn-small"
                    onClick={() => nextSongInPlaylist(2)}
                  >
                    <FaStepForward />
                  </Button>
                )}
              </div>
              
              <div className="progress-container">
                <span className="time-display">{audioRef2.current ? formatTime(audioRef2.current.currentTime) : '0:00'}</span>
                <Form.Range 
                  value={deck2.progress}
                  onChange={(e) => changeProgress(2, parseInt(e.target.value))}
                  className="progress-bar"
                />
                <span className="time-display">
                  {audioRef2.current && !isNaN(audioRef2.current.duration) 
                    ? formatTime(audioRef2.current.duration) 
                    : '0:00'}
                </span>
              </div>
              
              <div className="volume-container">
                <Form.Range 
                  value={deck2.volume}
                  onChange={(e) => changeVolume(2, parseInt(e.target.value))}
                  className="volume-slider"
                />
              </div>
            </div>
            
            <audio ref={audioRef2} />
          </div>
        </div>
        
        {/* Crossfader */}
        <div className="mixer-section">
          <div className="crossfader-container">
            <Form.Range 
              value={crossfader}
              onChange={(e) => setCrossfader(parseInt(e.target.value))}
              className="crossfader"
              min={0}
              max={100}
              step={1}
            />
            <div className="crossfader-labels">
              <span>A</span>
              <span>B</span>
            </div>
          </div>
          
          {/* Bouton de mix automatique */}
          <div className="auto-mix-container">
            <Button 
              variant={autoMixEnabled ? "success" : "outline-secondary"}
              className={`auto-mix-button ${autoMixEnabled ? 'active' : ''}`}
              onClick={toggleAutoMix}
            >
              <FaRandom /> Mix Auto {autoMixEnabled ? 'ON' : 'OFF'}
            </Button>
            {autoMixEnabled && (
              <div className="auto-mix-info">
                <small>Mix automatique {autoMixTimeBeforeEnd} sec. avant la fin</small>
                <Form.Range
                  value={autoMixTimeBeforeEnd}
                  onChange={(e) => setAutoMixTimeBeforeEnd(parseInt(e.target.value))}
                  min={5}
                  max={30}
                  step={1}
                  className="auto-mix-range"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Bibliothèque de chansons et playlists - Interface améliorée */}
      <Row className="library-container">
        <Col>
          <div className="library-header">
            <h2>Bibliothèque</h2>
            <div className="library-tabs">
              <Button 
                variant={activeTab === 'songs' ? 'primary' : 'outline-primary'}
                className="tab-button"
                onClick={() => setActiveTab('songs')}
              >
                <FaMusic /> Morceaux
              </Button>
              <Button 
                variant={activeTab === 'playlists' ? 'primary' : 'outline-primary'}
                className="tab-button"
                onClick={() => setActiveTab('playlists')}
              >
                <FaList /> Playlists
              </Button>
            </div>
          </div>
          
          <div className="search-container mb-3">
            <Form.Control 
              type="text" 
              placeholder="Rechercher..." 
              className="search-input"
            />
          </div>
          
          {activeTab === 'songs' ? (
            loading ? (
              <p className="text-center">Chargement des morceaux...</p>
            ) : (
              <div className="song-list">
                <div className="song-list-header">
                  <Row className="mx-0">
                    <Col xs={6}>Titre</Col>
                    <Col xs={4}>Artiste</Col>
                    <Col xs={2} className="text-center">Platine</Col>
                  </Row>
                </div>
                <div className="song-items-container">
                  {songs.map(song => (
                    <Row key={song._id} className="song-item">
                      <Col xs={6} className="song-title">{song.title}</Col>
                      <Col xs={4} className="song-artist">{song.artist}</Col>
                      <Col xs={2} className="text-center">
                        <div className="deck-buttons">
                          <Button 
                            variant="primary" 
                            size="sm"
                            className="deck-button deck-a"
                            onClick={() => loadSong(song, 1)}
                            title="Charger dans la platine A"
                          >
                            A
                          </Button>
                          <Button 
                            variant="danger" 
                            size="sm"
                            className="deck-button deck-b"
                            onClick={() => loadSong(song, 2)}
                            title="Charger dans la platine B"
                          >
                            B
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  ))}
                </div>
              </div>
            )
          ) : (
            loading ? (
              <p className="text-center">Chargement des playlists...</p>
            ) : (
              <div className="playlist-list">
                {playlists.map(playlist => (
                  <div key={playlist._id} className="playlist-card">
                    <div className="playlist-info">
                      <h3>{playlist.name}</h3>
                      <p>{playlist.songs ? playlist.songs.length : 0} morceaux</p>
                    </div>
                    <div className="playlist-actions">
                      <Button 
                        variant="primary" 
                        className="load-deck-button"
                        onClick={() => loadPlaylist(playlist, 1, 0)}
                      >
                        Charger Platine A
                      </Button>
                      <Button 
                        variant="danger" 
                        className="load-deck-button"
                        onClick={() => loadPlaylist(playlist, 2, 0)}
                      >
                        Charger Platine B
                      </Button>
                      <div className="song-selector-section">
                        <Button 
                          variant="secondary" 
                          className="song-selector-button" 
                          onClick={() => togglePlaylistSongs(playlist._id)}
                        >
                          Afficher les morceaux
                        </Button>
                        <div className={`playlist-songs-container ${expandedPlaylists[playlist._id] ? 'expanded' : 'collapsed'}`}>
                          <div className="dropdown-deck-selector">
                            <span>Titre</span>
                            <span>Actions</span>
                          </div>
                          {playlist.songs && playlist.songs.length > 0 ? (
                            playlist.songs.map((songItem, index) => {
                              // Recherche de la chanson complète dans la liste des chansons
                              let songTitle = "Morceau " + (index + 1);
                              let songId = typeof songItem === 'string' ? songItem : songItem._id;
                              
                              // Essayer de trouver la chanson complète dans la liste des chansons
                              const fullSong = songs.find(s => s._id === songId);
                              
                              if (fullSong) {
                                songTitle = fullSong.title || fullSong.name || `Morceau ${index + 1}`;
                              } else if (songItem.title) {
                                songTitle = songItem.title;
                              } else if (songItem.name) {
                                songTitle = songItem.name;
                              }
                              
                              return (
                                <div key={`${playlist._id}-song-${index}-${songId || index}`} className="dropdown-song-item">
                                  <span className="dropdown-song-title">
                                    {index + 1}. {songTitle}
                                  </span>
                                  <div className="dropdown-song-actions">
                                    <Button 
                                      size="sm" 
                                      variant="outline-primary"
                                      onClick={() => {
                                        console.log('Chargement dans platine A:', songItem);
                                        loadPlaylist(playlist, 1, index);
                                      }}
                                    >
                                      A
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline-danger"
                                      onClick={() => {
                                        console.log('Chargement dans platine B:', songItem);
                                        loadPlaylist(playlist, 2, index);
                                      }}
                                    >
                                      B
                                    </Button>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="dropdown-song-item">
                              <span className="dropdown-song-title">Aucun morceau dans cette playlist</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default DjMixer;
